// Implements a subset of the server API but backs onto Local Storage in the browser
import { computed, signal } from "@preact/signals";
import ObjectId from "ObjectId";
import * as _ from "underscore";

import * as readlangLocalStorage from "readlangLocalStorage";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as spacedRepetition from "shared/spacedRepetition";
import * as utils from "shared/utils";
import type { UserWordData } from "stores/UserWordStore";

export const limits = {
  books: 5,
  bookLength: 1024 * 50, // characters
  words: 500,
};
class LocalUserWordStore {
  readonly state = signal<UserWordData>({});
  readonly viewJSON = computed(() => ({ ...this.state.value }));

  constructor(attributes: UserWordData = {}) {
    this.state.value = { ...attributes };
  }

  hasField(key: string) {
    return (
      this.state.value[key] !== undefined && this.state.value[key] !== null
    );
  }

  patch(updates: Partial<UserWordData>) {
    this.state.value = { ...this.state.value, ...updates };
    return this;
  }

  save(updates: Partial<UserWordData> = {}) {
    this.patch(updates);
    return Promise.resolve(this);
  }
}

export const clearLocalStorage = (callback) => {
  readlangLocalStorage.clearWithExceptions(
    ["^mongoMachineId$", "^reader\\..+", "referrer", "couponToken"],
    callback
  );
};

const getItemJson = (key, callback) => {
  readlangLocalStorage.getItem(key, (value) => {
    if (value) {
      callback(JSON.parse(value));
    } else {
      callback(null);
    }
  });
};
const setItemJson = (key, value, callback) => {
  readlangLocalStorage.setItem(key, JSON.stringify(value), callback);
};

export const isLocalMode = async () =>
  (await readlangLocalStorage.getItemPromise("localMode")) === "true" ||
  // @ts-ignore
  (await readlangLocalStorage.getItemPromise("localMode")) === true;

export const setLocalMode = async (localMode) => {
  await readlangLocalStorage.setItemPromise("localMode", localMode);
};

/** @type {(callback: (userWords: Partial<UserWord>[]) => void) => void} */
const getUserWords = (callback) => {
  getItemJson("userWords", (result) => {
    callback(result || []);
  });
};
const setUserWords = (newUserWords, callback) => {
  // error checking for duplicates
  const wordLangMap = {};

  _.each(newUserWords, (newUserWord) => {
    const wordLang = newUserWord.word + "_" + newUserWord.language;
    wordLangMap[wordLang] = 1;
  });

  setItemJson("userWords", newUserWords, callback);
};

const getUser = (callback) => {
  getItemJson("user", (result) => {
    callback(
      result || {
        username: "guest",
        displayName: "Guest",
        currentLearningLanguage: "es",
        firstLanguage: "en",
        books: {},
        trialMode: true,
        _id: "local",
      }
    );
  });
};
const setUser = (newUser, callback) => {
  setItemJson("user", newUser, callback);
};

export const pushSync = (remoteRequest, callback) => {
  getUser((user) => {
    getUserWords(async (userWords) => {
      if (!(await isLocalMode())) {
        callback();
        return;
      }

      // go online
      setLocalMode(false);

      const numBooks = Object.keys(user.books).length || 0;

      const onlineUser: any = _.pick(
        user,
        "firstLanguage",
        "currentLearningLanguage",
        "preferredFirstLanguages",
        "acheivements",
        "readerFont",
        "readerAlign",
        "readerSentenceBreaks",
        "readerTranslationStyle",
        "readerMergePhrases",
        "readerWordHighlights",
        "bookmarkletPosition",
        "bookmarkletCollapsed",
        "bookmarkletColor"
      );

      onlineUser.addBooks = {};

      if (await readlangLocalStorage.getItemPromise("referrer")) {
        onlineUser.referrer =
          await readlangLocalStorage.getItemPromise("referrer");
      }

      if (await readlangLocalStorage.getItemPromise("httpReferrer")) {
        onlineUser.referrer =
          await readlangLocalStorage.getItemPromise("httpReferrer");
      }

      // sync user
      remoteRequest("PUT", "/api/user", onlineUser, (data) => {
        if (data.error) {
          console.error("push sync fail: %s", JSON.stringify(data));
          return;
        }
        readlangLocalStorage.removeItemPromise("referrer");
        readlangLocalStorage.removeItemPromise("httpReferrer");

        if (numBooks === 0 && userWords.length === 0) {
          clearLocalStorage(callback);
          return;
        }

        // add public books
        _.each(user.books, (userBookInfo, bookID) => {
          onlineUser.addBooks[bookID] = user.books[bookID];
        });

        // sync all books
        (async () => {
          for (const userWord of userWords) {
            await new Promise<void>((resolve) => {
              remoteRequest("POST", "/api/userWord", userWord, () => {
                resolve();
              });
            });
          }
          clearLocalStorage(() => {
            callback({ pushSyncSuccess: true });
          });
        })();
      });
    });
  });
};

export const request = async (
  method,
  path,
  data,
  callback,
  errorCallback,
  remoteRequest
) => {
  const splitURL = path.split("?");
  let splitPath = splitURL[0].split("/");

  data = data || {};

  const queryString = splitURL[1];
  if (queryString) {
    throw "Shouldn't use query string, use data instead";
  }

  if (splitPath[0] === "") {
    splitPath.splice(0, 1);
  }

  if (splitPath[0] !== "api") {
    return false;
  }

  let bookID;

  // -- Always try local API first for these --
  if (method === "GET" && splitPath[1] === "chunk" && splitPath.length > 2) {
    remoteRequest(method, path, data, callback, errorCallback);

    return true;
  }

  if (!(await isLocalMode())) {
    return false;
  }

  // -- Only use these if we are in local mode
  if (method === "POST" && splitPath[1] === "login") {
    remoteRequest(
      method,
      path,
      data,
      (responseData) => {
        pushSync(remoteRequest, (pushSyncData) => {
          callback(_.extend(responseData, pushSyncData || {}));
        });
      },
      errorCallback
    );
  } else if (method === "GET" && splitPath[1] === "webPushNotifications") {
    // Return empty list
    callback([]);
  } else if (method === "DELETE" && splitPath[1] === "webPushNotifications") {
    // no-op
    callback({});
  } else if (method === "GET" && splitPath[1] === "readyToStartFlashcards") {
    getUserWords((_userWords) => {
      getUser((user) => {
        const userWords = _.filter(
          _userWords,
          (userWord) =>
            userWord.language ===
            (data.language || user.currentLearningLanguage)
        );

        let countStarted = 0;
        let countReady = 0;
        _.each(userWords, (userWord) => {
          if ("interval" in userWord) {
            countStarted++;
          } else {
            countReady++;
          }
        });

        if (countStarted > 0) {
          callback({ error: "alreadyStarted" });
        } else {
          callback({ count: countReady });
        }
      });
    });
  } else if (method === "GET" && splitPath[1] === "listAllWords") {
    getUserWords((_userWords) => {
      getUser((user) => {
        let userWords = _.filter(
          _userWords,
          (userWord) =>
            userWord.language === (splitPath[2] || user.currentLearningLanguage)
        );

        if (data.filter === "readyToLearn") {
          const now = new Date();
          userWords = _.filter(userWords, (userWord) =>
            spacedRepetition.isDue(
              userWord.nextDate,
              now,
              dateUtils.getTimezoneOffset()
            )
          );
        }

        callback(_.pluck(userWords, "word"));
      });
    });
  } else if (
    method === "GET" &&
    splitPath[1] === "v2" &&
    splitPath[2] === "userWords"
  ) {
    if (!data.language || !data.exactPhrasesSearch) {
      return false;
    }

    const phraseRegExp = new RegExp(
      _.map(
        data.exactPhrasesSearch.split("|"),
        (phrase) => "^" + utils.regexpEscape(phrase) + "$"
      ).join("|"),
      "i"
    );

    getUserWords((_userWords) => {
      const userWords = _.filter(
        _userWords,
        (userWord) =>
          userWord.language === data.language &&
          phraseRegExp.test(userWord.word)
      );
      callback({
        count: userWords.length,
        userWords: userWords,
      });
    });
  } else if (method === "GET" && splitPath[1] === "user") {
    if (splitPath.length > 2) {
      return false; // handle remotely
    } else {
      getUser(callback);
    }
  } else if (
    method === "DELETE" &&
    splitPath[1] === "book" &&
    splitPath.length > 2
  ) {
    throw Error("Book deletion not supported in local mode");
  } else if (
    method === "DELETE" &&
    splitPath[1] === "userWord" &&
    splitPath[3] === "contexts"
  ) {
    // Mirrors the server: removes one context, deleting the word only if that
    // was its last
    const [, , userWordID, , contextID] = splitPath;
    getUserWords((userWords) => {
      const word = _.find(userWords, (thisWord) => thisWord._id === userWordID);
      if (!word) {
        callback({ error: "userWord not found" });
        return;
      }
      const contexts = (word.contexts ?? []).filter(
        (context) => context._id !== contextID
      );
      const deleted = contexts.length === 0;
      setUserWords(
        deleted
          ? _.without(userWords, word)
          : userWords.map((thisWord) =>
              thisWord === word ? { ...word, contexts } : thisWord
            ),
        () => callback({ deleted })
      );
    });
  } else if (
    method === "DELETE" &&
    splitPath[1] === "userWord" &&
    splitPath.length > 2
  ) {
    getUserWords((userWords) => {
      let index = -1;
      let id = splitPath[2];
      _.each(userWords, (word, i) => {
        if (word._id === id) {
          index = i;
          return false;
        }
      });

      if (index !== -1) {
        userWords.splice(index, 1);
        setUserWords(userWords, callback);
      } else {
        callback();
      }
    });
  } else if (
    method === "GET" &&
    splitPath[1] === "userWord" &&
    splitPath.length > 2
  ) {
    getUserWords((userWords) => {
      /** @type {any} */
      let theWord = -1;
      let id = splitPath[2];

      _.each(userWords, (word) => {
        if (word._id === id) {
          theWord = word;
          return false;
        }
      });

      if (theWord) {
        callback(theWord);
      } else {
        callback({ error: "not found" });
      }
    });
  } else if (
    method === "POST" &&
    splitPath[1] === "userWord" &&
    splitPath[3] === "recall"
  ) {
    getUser((user) => {
      const recallOptions = {
        ...spacedRepetition.reviewPaceSettingsFor(user?.reviewPace),
        timezoneOffset: dateUtils.getTimezoneOffset(),
      };
      getUserWords((userWords) => {
        const userWordID = splitPath[2];
        const word = _.find(
          userWords,
          (thisWord) => userWordID === thisWord._id
        );
        if (word) {
          const wordStore = new LocalUserWordStore(word);
          spacedRepetition.wordRecalled(
            wordStore,
            data.recallEase,
            new Date(),
            data.difficulty ?? 1,
            recallOptions
          );
          _.extend(word, wordStore.viewJSON.value);

          setUserWords(userWords, () => {
            callback({ success: true });
          });
        } else {
          callback({ error: "Word not found" });
        }
      });
    });
  } else if (
    method === "POST" &&
    /\/api\/userWord/.test(path) &&
    splitPath.length === 2
  ) {
    // lookedUpWhileReading is transport-only: it drives the failed-recall
    // update below and must not be persisted on the word
    const { lookedUpWhileReading, ...wordData } = data;
    getUser((user) => {
      const recallOptions = {
        ...spacedRepetition.reviewPaceSettingsFor(user?.reviewPace),
        timezoneOffset: dateUtils.getTimezoneOffset(),
      };
      getUserWords((userWords) => {
        let word: Partial<UserWord>;
        // check for existing
        if (languageMap.map[wordData.language].capitalizationImportant) {
          word = _.find(
            userWords,
            (thisWord) =>
              wordData.word === thisWord.word &&
              wordData.language === thisWord.language
          );
        } else {
          const regExp = new RegExp(
            "^" + utils.regexpEscape(wordData.word) + "$",
            "i"
          );
          word = _.find(
            userWords,
            (thisWord) =>
              regExp.test(thisWord.word) &&
              wordData.language === thisWord.language
          );
        }

        const result: any = { success: true };

        // add new context if exists
        if (word) {
          _.extend(word, _.omit(wordData, ["word", "translation"]));

          if (word.word !== wordData.word && /[a-z]/.test(wordData.word[0])) {
            // prefer version with lower case first letter
            word.word = wordData.word;
          }

          // A lookup while reading counts as a failed recall, matching the
          // server: the same update as pressing "didn't remember" (ease 0) in
          // a flashcard session. Words with no schedule yet are left alone.
          if (lookedUpWhileReading && word.nextDate) {
            const tempUserWord = new LocalUserWordStore(
              word as unknown as UserWordData
            );
            spacedRepetition.wordRecalled(
              tempUserWord,
              0,
              new Date(),
              1,
              recallOptions
            );
            _.extend(word, tempUserWord.viewJSON.value);
          }

          setUserWords(userWords, () => {
            result.updated = word._id;
            callback(result);
          });
        } else {
          if (userWords.length > limits.words) {
            callback({
              error:
                "The free trial version is limited to " +
                limits.words +
                " words, please signup for more.",
            });
            return;
          }

          word = {
            word: wordData.word,
            translation: wordData.translation,
            _id: new ObjectId().toString(),
            // no userID in localAPI
          };

          _.extend(word, _.omit(wordData, ["word", "translation"]));

          remoteRequest(
            "GET",
            "/api/wordFrequency",
            {
              language: word.language,
              word: word.word.toLowerCase(),
            },
            (response) => {
              if (response.frequency) {
                word.frequency = response.frequency;
                result.frequency = response.frequency;
              }
              userWords.push(word);
              setUserWords(userWords, () => {
                result.upserted = word._id;
                callback(result);
              });
            },
            () => {
              userWords.push(word);
              setUserWords(userWords, () => {
                result.upserted = word._id;
                callback(result);
              });
            }
          );
        }
      });
    });
  } else if (
    method === "PATCH" &&
    splitPath[1] === "book" &&
    splitPath[3] === "userBook"
  ) {
    bookID = splitPath[2];
    getUser((user) => {
      user.books[bookID] = _.extend(user.books[bookID] || {}, data);
      setUser(user, () => {
        callback({
          success: true,
        });
      });
    });
  } else if (method === "POST" && /\/api\/library/.test(path)) {
    throw Error("Not supported in local mode");
  } else if (
    (method === "PUT" || method === "PATCH") &&
    splitPath[1] === "userWord"
  ) {
    splitPath = path.split("?")[0].split("/");
    // recall only feeds the server's Recall history, which local mode lacks
    const { recall: _recall, ...wordData } = data;
    getUserWords((userWords) => {
      const userWordID = splitPath[splitPath.length - 1];
      const word = _.find(userWords, (thisWord) => userWordID === thisWord._id);
      if (word) {
        _.extend(word, wordData);
        setUserWords(userWords, () => {
          callback({ success: true });
        });
      } else {
        callback({ error: "Word not found" });
      }
    });
  } else if (
    (method === "PUT" || method === "PATCH") &&
    splitPath[1] === "user"
  ) {
    // no password stuff needed for local version
    getUser((user) => {
      const picked = _.pick(
        data,
        "firstLanguage",
        "currentLearningLanguage",
        "preferredFirstLanguages",
        "sortBooksBy",
        "publicBooksCategories",
        "acheivements",
        "username",
        "location",
        "homepageURL",
        "about",
        "customDictionaries",
        "typingMode",
        "passiveMode",
        "passiveModeLanguages",
        "publicBooksFilter",
        "readerFont",
        "readerAlign",
        "readerSentenceBreaks",
        "readerTranslationStyle",
        "readerMergePhrases",
        "readerWordHighlights",
        "readerTranslations",
        "bookmarkletPosition",
        "bookmarkletCollapsed",
        "bookmarkletColor",
        "reviewPace"
      );

      // Drop unrecognised reviewPace values so we never persist strings like
      // `toString` / `__proto__` that would later resolve to inherited
      // `Object.prototype` members when used as a lookup key in
      // `reviewPaceSettingsFor`.
      if (
        Object.prototype.hasOwnProperty.call(picked, "reviewPace") &&
        !spacedRepetition.isValidReviewPace(picked.reviewPace)
      ) {
        delete picked.reviewPace;
      }

      _.extend(user, picked);

      setUser(user, callback);
    });
  } else if (method === "GET" && /\/api\/logout/.test(path)) {
    if (
      confirm(
        "Warning - since you haven't registered, logging out will delete *all* your words!\n\n" +
          "Are you sure you want delete everything?"
      )
    ) {
      clearLocalStorage(callback);
    } else {
      callback();
    }
  } else if (method === "GET" && /\/api\/currentDate/.test(path)) {
    callback({ currentDate: new Date() });
  } else if (method === "GET" && /\/api\/groups/.test(path)) {
    callback({ adminGroups: [], memberGroups: [] });
  } else if (method === "GET" && splitPath[1] === "dashboard") {
    callback([]);
  } else {
    // Not handled locally, let the remote server handle it
    return false;
  }

  // indicate that we've handled this request
  return true;
};
